import { useMemo, useState } from 'react';
import {
  ETIQUETAS_CONDICION_PAGO,
  calcularDesdeNeto,
  evaluarFactura,
  formatearCLP,
  formatearPorcentajeSimple,
  type CondicionPago,
  type EstadoFactura,
  type TipoDocumentoVenta,
} from '@lexicore/shared';
import {
  IconoBasura,
  IconoCerrar,
  IconoEditar,
  IconoMas,
} from '../../componentes/Iconos';
import { Panel, Vacio } from '../../componentes/UI';
import { useAvisos } from '../../contextos/AvisosContext';
import type { Cuota, FacturaProyecto, ProyectoDetalle } from '../../lib/consultas';
import {
  useAnularFactura,
  useEliminarCuotaManual,
  useEliminarPago,
  useRegistrarFacturaDesdeCuota,
  useRegistrarFacturaLibre,
  useRegistrarPagoEnProyecto,
} from '../../lib/mutaciones';
import { ModalConfigurarCronograma } from './ModalConfigurarCronograma';
import { ModalCuotaManual } from './ModalCuotaManual';

/**
 * Facturación y cobranza del proyecto.
 *
 * Importante: esto **no emite documentos tributarios**. La factura electrónica
 * se emite donde corresponda y aquí se registra para llevar el control de qué
 * se cobró, qué falta y hace cuánto. Por eso la interfaz dice «registrar» y no
 * «emitir», y el campo de folio existe para anotar el del documento real.
 */
export function PestanaFacturacion({ proyecto }: { proyecto: ProyectoDetalle }) {
  const [registrandoCuota, setRegistrandoCuota] = useState<Cuota | null>(null);
  const [facturaLibre, setFacturaLibre] = useState(false);
  const [pagando, setPagando] = useState<FacturaProyecto | null>(null);
  const [anulando, setAnulando] = useState<FacturaProyecto | null>(null);

  // Modales de Gestión de Facturación y Cuotas
  const [configurandoCronograma, setConfigurandoCronograma] = useState(false);
  const [cuotaManualModal, setCuotaManualModal] = useState<{ visible: boolean; cuota?: Cuota | null }>({
    visible: false,
    cuota: null,
  });

  const avisos = useAvisos();
  const anular = useAnularFactura();
  const eliminarCuotaMut = useEliminarCuotaManual();

  /**
   * La API devuelve las facturas del proyecto con sus pagos pero sin la
   * situación derivada. Se calcula acá con el mismo motor que usa el servidor,
   * en vez de duplicar la regla de negocio.
   */
  const facturas = useMemo(
    () =>
      proyecto.facturas.map((factura) => ({
        factura,
        situacion: evaluarFactura({
          tipoDocumento: factura.tipoDocumento as TipoDocumentoVenta,
          estado: factura.estado as EstadoFactura,
          fechaEmision: factura.fechaEmision,
          fechaVencimiento: factura.fechaVencimiento,
          montoTotal: factura.montoTotal,
          pagos: factura.pagos,
        }),
      })),
    [proyecto.facturas],
  );

  const totales = useMemo(() => {
    const facturado = facturas
      .filter(({ factura }) => factura.estado !== 'ANULADA' && factura.estado !== 'BORRADOR')
      .reduce((suma, { factura }) => suma + factura.montoTotal, 0);
    const cobrado = facturas.reduce((suma, { situacion }) => suma + situacion.montoPagado, 0);
    const porCobrar = facturas.reduce((suma, { situacion }) => suma + situacion.saldo, 0);
    const proyectado = proyecto.cuotas
      .filter((cuota) => cuota.estado === 'PROYECTADA')
      .reduce((suma, cuota) => suma + cuota.montoTotal, 0);

    return { facturado, cobrado, porCobrar, proyectado };
  }, [facturas, proyecto.cuotas]);

  const balanceFacturacion = useMemo(() => {
    const contratoNeto = proyecto.montoContratoNeto;
    const contratoBruto = proyecto.montoContratoBruto || Math.round(contratoNeto * 1.19);

    const cuotasNeto = proyecto.cuotas.reduce((sum, c) => sum + c.montoNeto, 0);
    const cuotasBruto = proyecto.cuotas.reduce((sum, c) => sum + c.montoTotal, 0);

    const saldoPorProgramarNeto = contratoNeto - cuotasNeto;
    const saldoPorProgramarBruto = contratoBruto - cuotasBruto;

    const facturadoNeto = facturas
      .filter(({ factura }) => factura.estado !== 'ANULADA' && factura.estado !== 'BORRADOR')
      .reduce((sum, { factura }) => sum + factura.montoNeto, 0);

    return {
      contratoNeto,
      contratoBruto,
      cuotasNeto,
      cuotasBruto,
      saldoPorProgramarNeto,
      saldoPorProgramarBruto,
      facturadoNeto,
    };
  }, [proyecto, facturas]);

  const ejecutarAnulacion = async (factura: FacturaProyecto, motivo: string) => {
    try {
      await anular.mutateAsync({ id: factura.id, motivo: motivo || undefined });
      avisos.exito('Factura anulada. Su cuota vuelve al cronograma como proyectada.');
      setAnulando(null);
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo anular la factura.');
    }
  };

  const ejecutarEliminarCuota = async (cuotaId: string) => {
    try {
      await eliminarCuotaMut.mutateAsync(cuotaId);
      avisos.exito('Cuota eliminada correctamente del cronograma.');
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo eliminar la cuota.');
    }
  };

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: 14,
          marginBottom: 20,
        }}
      >
        {[
          { etiqueta: 'Facturado', valor: totales.facturado, color: 'var(--text-primary)' },
          { etiqueta: 'Cobrado', valor: totales.cobrado, color: 'var(--accent-green)' },
          { etiqueta: 'Por cobrar', valor: totales.porCobrar, color: 'var(--accent-yellow)' },
          { etiqueta: 'Aún por facturar', valor: totales.proyectado, color: 'var(--text-muted)' },
        ].map((celda) => (
          <div
            key={celda.etiqueta}
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-sm)',
              padding: '12px 16px',
            }}
          >
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{celda.etiqueta}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 17, fontWeight: 700, marginTop: 4, color: celda.color }}>
              {formatearCLP(celda.valor)}
            </div>
          </div>
        ))}
      </div>

      {/* ── Barra de Balance Financiero de Facturación ── */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-md)',
          padding: '16px 20px',
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
            Balance de Facturación y Cuotas
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
            {proyecto.cuotas.length} cuota(s) programada(s) · {proyecto.cuotas.filter((c) => c.estado === 'FACTURADA').length} facturada(s)
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 12,
          }}
        >
          <div style={{ padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Valor Contrato Neto</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
              {formatearCLP(balanceFacturacion.contratoNeto)}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Bruto: {formatearCLP(balanceFacturacion.contratoBruto)}</div>
          </div>

          <div style={{ padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Total en Cuotas (Neto)</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700, color: 'var(--accent-blue)' }}>
              {formatearCLP(balanceFacturacion.cuotasNeto)}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>Bruto: {formatearCLP(balanceFacturacion.cuotasBruto)}</div>
          </div>

          <div style={{ padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Total Facturado (Neto)</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 700, color: 'var(--accent-green)' }}>
              {formatearCLP(balanceFacturacion.facturadoNeto)}
            </div>
          </div>

          <div style={{ padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-sm)' }}>
            <div style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>Saldo por Programar (Neto)</div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 15,
                fontWeight: 700,
                color:
                  balanceFacturacion.saldoPorProgramarNeto === 0
                    ? 'var(--accent-green)'
                    : balanceFacturacion.saldoPorProgramarNeto > 0
                    ? 'var(--accent-yellow)'
                    : 'var(--accent-red)',
              }}
            >
              {formatearCLP(balanceFacturacion.saldoPorProgramarNeto)}
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              {balanceFacturacion.saldoPorProgramarNeto === 0
                ? 'Cuotas cuadran con contrato'
                : balanceFacturacion.saldoPorProgramarNeto > 0
                ? 'Falta programar en cuotas'
                : 'Cuotas exceden contrato'}
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          fontSize: 11.5,
          color: 'var(--text-muted)',
          lineHeight: 1.55,
          padding: '11px 14px',
          background: 'var(--accent-blue-soft)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 20,
        }}
      >
        Este módulo lleva el control de cobranza; no emite documentos tributarios. Registra aquí las facturas que emitas
        en tu sistema de facturación y anota su folio para poder cruzarlas.
      </div>

      {/* ── Cronograma ── */}
      <Panel
        titulo="Cronograma de cobranza"
        subtitulo={`Modo: ${proyecto.modoFacturacion.replace('_', ' ').toLowerCase()}`}
        acciones={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setConfigurandoCronograma(true)}
            >
              <IconoEditar size={13} />
              Configurar / Regenerar Facturación
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setCuotaManualModal({ visible: true, cuota: null })}
            >
              <IconoMas size={13} />
              Añadir Cuota Manual
            </button>
          </div>
        }
        style={{ marginBottom: 16 }}
      >
        {proyecto.cuotas.length === 0 ? (
          <Vacio
            titulo="Sin cuotas programadas"
            detalle="El cronograma se genera al adjudicar la licitación, según el modo de facturación del proyecto. Haz clic en Configurar / Regenerar Facturación para crearlo."
          />
        ) : (
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>Cuota / Concepto</th>
                  <th>Cobro estimado</th>
                  <th style={{ textAlign: 'right' }}>Neto</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th>Estado</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {proyecto.cuotas.map((cuota) => {
                  const hitoAsociado = cuota.hitoId
                    ? proyecto.hitos.find((h) => h.id === cuota.hitoId)
                    : null;
                  const facturaAsociada = cuota.facturaId
                    ? proyecto.facturas.find((f) => f.id === cuota.facturaId)
                    : null;

                  return (
                    <tr key={cuota.id}>
                      <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                        {cuota.descripcion}
                        {hitoAsociado && (
                          <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
                            Hito: {hitoAsociado.nombre}
                          </div>
                        )}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>
                        {cuota.fechaCobroEstimada ? cuota.fechaCobroEstimada.slice(0, 10) : cuota.fechaEmisionEstimada.slice(0, 10)}
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                          Emisión: {cuota.fechaEmisionEstimada.slice(0, 10)}
                        </div>
                      </td>
                      <td className="td-mono">{formatearCLP(cuota.montoNeto)}</td>
                      <td className="td-mono" style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                        {formatearCLP(cuota.montoTotal)}
                      </td>
                      <td>
                        <span className={`factura-badge ${cuota.estado === 'FACTURADA' ? 'PAGADA' : 'PENDIENTE'}`}>
                          {cuota.estado}
                        </span>
                        {facturaAsociada && (
                          <div style={{ fontSize: 10, color: 'var(--accent-green)', fontWeight: 600, marginTop: 2 }}>
                            Doc N° {facturaAsociada.numero ?? facturaAsociada.id.slice(0, 8)}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                          {cuota.estado === 'PROYECTADA' ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setRegistrandoCuota(cuota)}
                                style={{ fontSize: 11, padding: '4px 8px' }}
                              >
                                Registrar factura
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => setCuotaManualModal({ visible: true, cuota })}
                                title="Editar cuota"
                                style={{ padding: '4px 7px' }}
                              >
                                <IconoEditar size={12} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm"
                                onClick={() => void ejecutarEliminarCuota(cuota.id)}
                                title="Eliminar cuota proyectada"
                                style={{ padding: '4px 7px', color: 'var(--accent-red)' }}
                                disabled={eliminarCuotaMut.isPending}
                              >
                                <IconoBasura size={12} />
                              </button>
                            </>
                          ) : (
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              Facturada
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* ── Facturas ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 16, flexWrap: 'wrap' }}>
        <h3 className="panel-titulo">Facturas registradas</h3>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setFacturaLibre(true)}>
          <IconoMas size={13} />
          Factura sin cuota
        </button>
      </div>

      <div className="tabla-wrap">
        <div className="tabla-scroll">
          <table style={{ minWidth: 980 }}>
            <thead>
              <tr>
                <th>N° / folio</th>
                <th>Fechas</th>
                <th style={{ textAlign: 'right' }}>Neto</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'right' }}>Pagado</th>
                <th style={{ textAlign: 'right' }}>Saldo</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {facturas.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <Vacio
                      titulo="Todavía no hay facturas registradas"
                      detalle="Registra una desde el cronograma de arriba, o crea una factura suelta para un cobro que no estaba planificado."
                    />
                  </td>
                </tr>
              )}

              {facturas.map(({ factura, situacion }) => (
                <tr key={factura.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-primary)' }}>
                    {factura.numero ?? 's/n'}
                    {factura.folioSii && (
                      <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>folio {factura.folioSii}</div>
                    )}
                  </td>
                  <td style={{ fontSize: 11 }}>
                    <div style={{ fontFamily: 'var(--font-mono)' }}>{factura.fechaEmision.slice(0, 10)}</div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                      vence {factura.fechaVencimiento.slice(0, 10)} ·{' '}
                      {ETIQUETAS_CONDICION_PAGO[factura.condicionPago as CondicionPago] ?? factura.condicionPago}
                    </div>
                  </td>
                  <td className="td-mono">{formatearCLP(factura.montoNeto)}</td>
                  <td className="td-mono" style={{ color: 'var(--text-primary)' }}>{formatearCLP(factura.montoTotal)}</td>
                  <td className="td-mono" style={{ color: 'var(--accent-green)' }}>
                    {formatearCLP(situacion.montoPagado)}
                    {situacion.pagadoPct > 0 && situacion.pagadoPct < 100 && (
                      <div style={{ fontSize: 9.5, color: 'var(--text-muted)' }}>
                        {formatearPorcentajeSimple(situacion.pagadoPct)}
                      </div>
                    )}
                  </td>
                  <td className="td-mono" style={{ color: situacion.saldo > 0 ? 'var(--accent-yellow)' : 'var(--text-muted)' }}>
                    {formatearCLP(situacion.saldo)}
                  </td>
                  <td>
                    <span className={`factura-badge ${situacion.estado}`}>{situacion.estado}</span>
                    {situacion.estaVencida && (
                      <div style={{ fontSize: 9.5, color: 'var(--accent-red)', fontWeight: 600, marginTop: 4 }}>
                        {situacion.diasAtraso} días de atraso
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {situacion.saldo > 0 && factura.estado !== 'ANULADA' && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setPagando(factura)}>
                          Pago
                        </button>
                      )}
                      {factura.estado !== 'ANULADA' && (
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setAnulando(factura)}
                          style={{ color: 'var(--accent-red)' }}
                        >
                          Anular
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagos ya registrados, para poder corregir uno mal cargado. */}
      {facturas.some(({ factura }) => factura.pagos.length > 0) && (
        <Panel titulo="Pagos recibidos" style={{ marginTop: 16 }}>
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Factura</th>
                  <th>Medio</th>
                  <th>Referencia</th>
                  <th style={{ textAlign: 'right' }}>Monto</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {facturas.flatMap(({ factura }) =>
                  factura.pagos.map((pago) => (
                    <FilaPago key={pago.id} pago={pago} numeroFactura={factura.numero} />
                  )),
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {configurandoCronograma && (
        <ModalConfigurarCronograma
          proyecto={proyecto}
          alCerrar={() => setConfigurandoCronograma(false)}
        />
      )}
      {cuotaManualModal.visible && (
        <ModalCuotaManual
          proyecto={proyecto}
          cuota={cuotaManualModal.cuota}
          alCerrar={() => setCuotaManualModal({ visible: false, cuota: null })}
        />
      )}
      {registrandoCuota && (
        <DialogoRegistrarDesdeCuota cuota={registrandoCuota} alCerrar={() => setRegistrandoCuota(null)} />
      )}
      {facturaLibre && <DialogoFacturaLibre proyecto={proyecto} alCerrar={() => setFacturaLibre(false)} />}
      {pagando && (
        <DialogoPagoProyecto
          factura={pagando}
          saldo={facturas.find(({ factura }) => factura.id === pagando.id)?.situacion.saldo ?? 0}
          alCerrar={() => setPagando(null)}
        />
      )}
      {anulando && (
        <DialogoAnular
          factura={anulando}
          tienePagos={anulando.pagos.length > 0}
          alConfirmar={(motivo) => void ejecutarAnulacion(anulando, motivo)}
          alCerrar={() => setAnulando(null)}
          enviando={anular.isPending}
        />
      )}
    </>
  );
}

function FilaPago({
  pago,
  numeroFactura,
}: {
  pago: { id: string; fecha: string; monto: number; medio: string; referencia: string | null };
  numeroFactura: string | null;
}) {
  const avisos = useAvisos();
  const eliminar = useEliminarPago();

  const borrar = async () => {
    try {
      await eliminar.mutateAsync(pago.id);
      avisos.exito('Pago eliminado. El saldo de la factura se recalculó.');
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo eliminar el pago.');
    }
  };

  return (
    <tr>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5 }}>{pago.fecha.slice(0, 10)}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-primary)' }}>
        {numeroFactura ?? 's/n'}
      </td>
      <td>{pago.medio}</td>
      <td>{pago.referencia ?? '—'}</td>
      <td className="td-mono" style={{ color: 'var(--accent-green)' }}>{formatearCLP(pago.monto)}</td>
      <td>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => void borrar()}
          disabled={eliminar.isPending}
          style={{ color: 'var(--accent-red)' }}
          aria-label="Eliminar este pago"
        >
          <IconoCerrar size={12} />
        </button>
      </td>
    </tr>
  );
}

function DialogoRegistrarDesdeCuota({ cuota, alCerrar }: { cuota: Cuota; alCerrar: () => void }) {
  const avisos = useAvisos();
  const registrar = useRegistrarFacturaDesdeCuota();

  const [numero, setNumero] = useState('');
  const [fechaEmision, setFechaEmision] = useState(new Date().toISOString().slice(0, 10));

  const guardar = async () => {
    try {
      await registrar.mutateAsync({ cuotaId: cuota.id, numero: numero.trim() || undefined, fechaEmision });
      avisos.exito('Factura registrada. La cuota queda marcada como facturada.');
      alCerrar();
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo registrar la factura.');
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && alCerrar()} role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>Registrar factura</h2>
        </div>

        <div className="modal-body">
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 18, lineHeight: 1.55 }}>
            {cuota.descripcion} ·{' '}
            <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>
              {formatearCLP(cuota.montoTotal)}
            </strong>
            <br />
            Hereda el monto, la condición de pago y el número de orden de compra del proyecto.
          </p>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="numero">N° de factura</label>
              <input id="numero" value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="El de tu sistema de facturación" autoFocus />
            </div>

            <div className="form-group">
              <label htmlFor="emision">Fecha de emisión</label>
              <input id="emision" type="date" value={fechaEmision} onChange={(e) => setFechaEmision(e.target.value)} />
              <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                El vencimiento se calcula desde aquí y la condición de pago.
              </span>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={alCerrar}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void guardar()} disabled={registrar.isPending}>
            {registrar.isPending ? 'Registrando…' : 'Registrar factura'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DialogoFacturaLibre({ proyecto, alCerrar }: { proyecto: ProyectoDetalle; alCerrar: () => void }) {
  const avisos = useAvisos();
  const registrar = useRegistrarFacturaLibre();

  const [numero, setNumero] = useState('');
  const [folioSii, setFolioSii] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState('FACTURA_ELECTRONICA');
  const [fechaEmision, setFechaEmision] = useState(new Date().toISOString().slice(0, 10));
  const [condicionPago, setCondicionPago] = useState('DIAS_30');
  const [montoNeto, setMontoNeto] = useState(0);

  const desglose = calcularDesdeNeto(montoNeto, 19);
  const sinOrganismo = !proyecto.organismo;

  const guardar = async () => {
    if (!proyecto.organismo) return;

    try {
      await registrar.mutateAsync({
        proyectoId: proyecto.id,
        organismoId: proyecto.organismo.id,
        numero: numero.trim() || undefined,
        folioSii: folioSii.trim() || undefined,
        tipoDocumento,
        fechaEmision,
        condicionPago,
        montoNeto,
        estado: 'EMITIDA',
        numeroOrdenCompra: proyecto.numeroOrdenCompra ?? undefined,
      });
      avisos.exito('Factura registrada');
      alCerrar();
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo registrar la factura.');
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && alCerrar()} role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-header">
          <h2>Factura sin cuota</h2>
        </div>

        <div className="modal-body">
          {sinOrganismo ? (
            <Vacio
              titulo="Este proyecto no tiene organismo asignado"
              detalle="No se sabe a quién facturarle. Asígnalo desde los datos del proyecto y vuelve a intentarlo."
            />
          ) : (
            <>
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 18 }}>
                Para {proyecto.organismo?.nombre}. Úsala cuando el cobro no estaba en el cronograma.
              </p>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="tipoDoc">Tipo de documento</label>
                  <select id="tipoDoc" value={tipoDocumento} onChange={(e) => setTipoDocumento(e.target.value)}>
                    <option value="FACTURA_ELECTRONICA">Factura electrónica</option>
                    <option value="FACTURA_EXENTA">Factura exenta</option>
                    <option value="BOLETA">Boleta</option>
                    <option value="NOTA_CREDITO">Nota de crédito</option>
                    <option value="NOTA_DEBITO">Nota de débito</option>
                  </select>
                  {tipoDocumento === 'NOTA_CREDITO' && (
                    <span style={{ fontSize: 10.5, color: 'var(--accent-yellow)' }}>
                      Una nota de crédito resta del total por cobrar.
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="numeroLibre">N° de documento</label>
                  <input id="numeroLibre" value={numero} onChange={(e) => setNumero(e.target.value)} autoFocus />
                </div>

                <div className="form-group">
                  <label htmlFor="folio">Folio</label>
                  <input id="folio" value={folioSii} onChange={(e) => setFolioSii(e.target.value)} placeholder="Opcional" />
                </div>

                <div className="form-group">
                  <label htmlFor="emisionLibre">Fecha de emisión</label>
                  <input id="emisionLibre" type="date" value={fechaEmision} onChange={(e) => setFechaEmision(e.target.value)} />
                </div>

                <div className="form-group">
                  <label htmlFor="condicion">Condición de pago</label>
                  <select id="condicion" value={condicionPago} onChange={(e) => setCondicionPago(e.target.value)}>
                    <option value="CONTADO">Contado</option>
                    <option value="DIAS_15">15 días</option>
                    <option value="DIAS_30">30 días</option>
                    <option value="DIAS_45">45 días</option>
                    <option value="DIAS_60">60 días</option>
                    <option value="DIAS_90">90 días</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="netoLibre">Monto neto</label>
                  <input id="netoLibre" type="number" value={montoNeto || ''} onChange={(e) => setMontoNeto(Number(e.target.value))} min={0} />
                  {montoNeto > 0 && (
                    <span style={{ fontSize: 10.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      IVA {formatearCLP(desglose.montoIva)} · total {formatearCLP(desglose.montoTotal)}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={alCerrar}>Cancelar</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void guardar()}
            disabled={sinOrganismo || montoNeto <= 0 || registrar.isPending}
          >
            {registrar.isPending ? 'Registrando…' : 'Registrar factura'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DialogoPagoProyecto({
  factura,
  saldo,
  alCerrar,
}: {
  factura: FacturaProyecto;
  saldo: number;
  alCerrar: () => void;
}) {
  const avisos = useAvisos();
  const registrar = useRegistrarPagoEnProyecto();

  const [monto, setMonto] = useState(saldo);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [medio, setMedio] = useState('TRANSFERENCIA');
  const [referencia, setReferencia] = useState('');

  const excede = monto > saldo;

  const guardar = async () => {
    try {
      await registrar.mutateAsync({ facturaId: factura.id, fecha, monto, medio, referencia: referencia || undefined });
      avisos.exito(`Pago de ${formatearCLP(monto)} registrado`);
      alCerrar();
    } catch (error) {
      avisos.error(error instanceof Error ? error.message : 'No se pudo registrar el pago.');
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && alCerrar()} role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>Registrar pago · {factura.numero ?? 'factura sin número'}</h2>
        </div>

        <div className="modal-body">
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 18 }}>
            Saldo pendiente de{' '}
            <strong style={{ color: 'var(--accent-yellow)', fontFamily: 'var(--font-mono)' }}>{formatearCLP(saldo)}</strong>
          </p>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="montoPago">Monto</label>
              <input id="montoPago" type="number" value={monto || ''} onChange={(e) => setMonto(Number(e.target.value))} min={1} autoFocus />
              {excede && (
                <span className="form-error">
                  Supera el saldo. Si pagaron de más, registra el saldo y emite una nota de crédito por la diferencia.
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="fechaPagoF">Fecha del pago</label>
              <input id="fechaPagoF" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </div>

            <div className="form-group">
              <label htmlFor="medioPago">Medio</label>
              <select id="medioPago" value={medio} onChange={(e) => setMedio(e.target.value)}>
                <option value="TRANSFERENCIA">Transferencia</option>
                <option value="CHEQUE">Cheque</option>
                <option value="EFECTIVO">Efectivo</option>
                <option value="COMPENSACION">Compensación</option>
                <option value="OTRO">Otro</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="refPago">Referencia</label>
              <input id="refPago" value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="N° de operación" />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={alCerrar}>Cancelar</button>
          <button type="button" className="btn btn-primary" onClick={() => void guardar()} disabled={registrar.isPending || excede || monto <= 0}>
            {registrar.isPending ? 'Guardando…' : 'Registrar pago'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DialogoAnular({
  factura,
  tienePagos,
  alConfirmar,
  alCerrar,
  enviando,
}: {
  factura: FacturaProyecto;
  tienePagos: boolean;
  alConfirmar: (motivo: string) => void;
  alCerrar: () => void;
  enviando: boolean;
}) {
  const [motivo, setMotivo] = useState('');

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && alCerrar()} role="dialog" aria-modal="true">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>Anular factura {factura.numero ?? 's/n'}</h2>
        </div>

        <div className="modal-body">
          {tienePagos ? (
            <div
              style={{
                padding: '14px 16px',
                background: 'var(--accent-red-soft)',
                color: 'var(--accent-red)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
                lineHeight: 1.6,
              }}
            >
              Esta factura ya tiene pagos registrados, así que no se puede anular — quedarían sin respaldo. Emite una
              nota de crédito por el monto correspondiente desde «Factura sin cuota».
            </div>
          ) : (
            <>
              <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
                La factura queda anulada y su cuota vuelve al cronograma como proyectada, para que el cobro no
                desaparezca solo porque se anuló el documento.
              </p>

              <div className="form-group">
                <label htmlFor="motivo">Motivo</label>
                <input id="motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Opcional" autoFocus />
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={alCerrar}>
            {tienePagos ? 'Entendido' : 'Cancelar'}
          </button>
          {!tienePagos && (
            <button type="button" className="btn btn-primary" onClick={() => alConfirmar(motivo)} disabled={enviando}>
              {enviando ? 'Anulando…' : 'Anular factura'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
